import { QueryFactory, RoleDto } from '@lmt/shared-types';
import { UpdateUserDto } from '@lmt/shared-types';
import { permissionsMappings } from '@lmt/unicorn-shared/app/api/auth/token-decoder';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { MultiSelectDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { tenantAdministratorRoleName } from '@lmt/unicorn-shared/app/components/users/list/UserList';
import { useRoles } from '@lmt/unicorn-shared/app/features/roles/selectors';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  emailRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useTranslation } from 'react-i18next';
import { snakeCase } from 'lodash';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useEventCallback } from '@mui/material';

const nameof = nameofFactory<UpdateUserDto>();

export interface EditUserProps {
  userId: string;
}

export const EditUser: FC<FormComponentProps & EditUserProps> = (props) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    userId,
    loadBlocking,
    registerFormDirtyStateRetriever,
  } = props;
  const i18next = useScopedTranslation('users.form');
  const i18n = useTranslation();
  const subtenants = useSubtenants();
  const roles = useRoles();
  const claims = useClaims();
  const modals = useModal();
  const queryClient = useQueryClient();

  const userQuery = QueryFactory.UserQuery.useGetUserQuery(props.userId);
  useEffect(() => {
    // this doesn't actually refetch the form, it's here just to return Promise and call loadBlocking.
    // Probably we need some other API to block the form.
    loadBlocking(formId, i18next.t('loading'), () => userQuery.refetch());
  }, []);

  const [prevSubtenants, setPrevSubtenants] = useState<number[]>(
    userQuery.data?.subtenants || [],
  );

  const isDisabledRole = useCallback((role: RoleDto) => {
    return !role.permissions?.every((rolePermission) =>
      Object.entries(claims.permissions).some(
        (entry) =>
          entry[0].toLowerCase() === rolePermission.toLowerCase() && entry[1],
      ),
    );
  }, []);

  const form = useAdvancedForm<UpdateUserDto>(
    useEventCallback(async (data) => {
      try {
        await tryCompleteForm(formType, formId, async () => {
          await QueryFactory.UserClient.editUser(userId!, data);
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.UserQuery.getUserQueryKey(props.userId),
          });
        });
        toast(i18next.t('edit_successful_toast'), {
          type: 'success',
        });
      } catch (e) {
        const errorDetails = handleSubmitFormError(e, form.setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }),
    userQuery.data,
    {
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );
  const formDirtyFields = form.formState.dirtyFields;
  useEffect(() => {
    registerFormDirtyStateRetriever(
      formId,
      () => Object.keys(formDirtyFields).length > 0,
    );
  }, [formDirtyFields]);

  const selectedRoles = form.watch('roles') || [];

  const isAccessAllSubtenants = useMemo(() => {
    return roles
      .filter((role) => selectedRoles.includes(role.id))
      .some((role) =>
        role.permissions?.some(
          (permission) =>
            permission === permissionsMappings.accessToAllSubtenants,
        ),
      );
  }, [selectedRoles]);

  useEffect(() => {
    if (isAccessAllSubtenants) {
      setPrevSubtenants(form.getValues('subtenants') || []);
      // we save a previous state of Subtenants field
      // so that we can return to it after deactivating a role with access to all the subtenants in Roles field
      form.setValue(
        'subtenants',
        subtenants.map((s) => s.id),
      );
      form.clearErrors('subtenants');
    } else {
      form.setValue('subtenants', prevSubtenants);
    }
  }, [isAccessAllSubtenants, subtenants]);

  return (
    <form
      className={styles.autoSizedContainer}
      onSubmit={form.handleSubmitDefault}
      data-test-id="editUserPopup"
    >
      <div className={styles.dataContainer}>
        <div className={styles.firstRow}>
          <Field
            className={styles.middleField}
            title={i18next.t('first_name')}
            required={true}
          >
            <Input
              {...form.register(nameof('firstName'), requiredRule())}
              errorText={form.formState.errors.firstName?.message}
            />
          </Field>
          <Field
            className={styles.field}
            title={i18next.t('last_name')}
            required={true}
          >
            <Input
              {...form.register(nameof('lastName'), requiredRule())}
              errorText={form.formState.errors.lastName?.message}
            />
          </Field>
        </div>
        <div className={styles.row}>
          <Field
            className={styles.field}
            title={i18next.t('email')}
            required={true}
          >
            <Input
              {...form.register(nameof('email'), {
                ...requiredRule(),
                ...emailRule(),
              })}
              errorText={form.formState.errors.email?.message}
            />
          </Field>
        </div>
        {subtenants.length > 1 && (
          <>
            <Field
              title={i18next.t('subtenants')}
              className={styles.field}
              required={true}
              hint={
                isAccessAllSubtenants
                  ? i18next.t('hint_select_subtenants_disabled', {
                      roleName: i18n.t(
                        `roles.list.default_roles.${snakeCase(tenantAdministratorRoleName)}`,
                        tenantAdministratorRoleName,
                      ),
                    })
                  : undefined
              }
            >
              <Controller
                name={'subtenants'}
                defaultValue={[]}
                control={form.control}
                rules={requiredRule()}
                render={({ field: { onChange, value } }) => (
                  <MultiSelectDropDownInput
                    disabled={isAccessAllSubtenants}
                    errorText={
                      (form.formState.errors.subtenants as any)?.message
                    }
                    options={subtenants}
                    idFunction={'id'}
                    getOptionLabel={'name'}
                    placeholder={i18next.t('select_subtenants_empty_label')}
                    allSelectedLabel={i18next.t(
                      'select_subtenants_all_selected',
                    )}
                    onValueChanged={(subtenants) => {
                      onChange(subtenants.map((s) => s.id));
                    }}
                    values={value}
                    variant="formInput"
                    headerTitle={i18next.t('subtenants')}
                  />
                )}
              />
            </Field>
          </>
        )}
        <Field
          title={i18next.t('roles')}
          className={styles.field}
          required={true}
        >
          <Controller
            name={'roles'}
            defaultValue={[]}
            control={form.control}
            rules={requiredRule()}
            render={({ field: { onChange, value } }) => (
              <MultiSelectDropDownInput
                errorText={(form.formState.errors.roles as any)?.message}
                options={roles
                  .map((x) => {
                    const displayValue: string = i18n.t(
                      `roles.list.default_roles.${snakeCase(x.name)}`,
                      x.name,
                    );
                    return {
                      ...x,
                      displayValue,
                    };
                  })
                  .sort((a, b) => a.displayValue.localeCompare(b.displayValue))}
                idFunction={'id'}
                getOptionLabel={(item) =>
                  i18n.t(
                    `roles.list.default_roles.${snakeCase(item.name)}`,
                    item.name,
                  )
                }
                hiddenFunc={isDisabledRole}
                placeholder={i18next.t('select_roles_empty_label')}
                allSelectedLabel={i18next.t('select_roles_all_selected')}
                onValueChanged={(roles) => {
                  onChange(roles.map((s) => s.id));
                }}
                values={value}
                variant="formInput"
                headerTitle={i18next.t('roles')}
              />
            )}
          />
        </Field>
        <CheckBox
          className={styles.field}
          title={i18next.t('must_update_password')}
          {...form.register(nameof('mustUpdatePassword'))}
        />
      </div>

      <div className={styles.submitContainer}>
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={styles.submit}
          title={i18next.t('edit_button')}
        />
      </div>
    </form>
  );
};
